import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

export interface VideoTutorial {
  id: string;
  title: string;
  youtubeId: string;
  thumbnail?: string;
  order?: number;
  isActive: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

const COLLECTION_NAME = 'video_tutorials';

/**
 * Extract YouTube video ID from various YouTube URL formats
 */
export function extractYouTubeId(url: string): string | null {
  if (!url) return null;
  
  // Handle different YouTube URL formats
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  
  // If it's already just an ID (11 characters, alphanumeric, dashes, underscores)
  if (/^[a-zA-Z0-9_-]{11}$/.test(url.trim())) {
    return url.trim();
  }
  
  return null;
}

/**
 * Get all video tutorials ordered by order field
 */
export async function getVideoTutorials(): Promise<VideoTutorial[]> {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      orderBy('order', 'asc')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as VideoTutorial));
  } catch (error) {
    console.error('Error fetching video tutorials:', error);
    throw error;
  }
}

/**
 * Get active video tutorials only
 */
export async function getActiveVideoTutorials(): Promise<VideoTutorial[]> {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      orderBy('order', 'asc')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data(),
      } as VideoTutorial))
      .filter(tutorial => tutorial.isActive !== false);
  } catch (error) {
    console.error('Error fetching active video tutorials:', error);
    throw error;
  }
}

/**
 * Get a single video tutorial by ID
 */
export async function getVideoTutorial(id: string): Promise<VideoTutorial | null> {
  try {
    const docRef = doc(db, COLLECTION_NAME, id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data(),
      } as VideoTutorial;
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching video tutorial:', error);
    throw error;
  }
}

/**
 * Create a new video tutorial
 */
export async function createVideoTutorial(
  data: Omit<VideoTutorial, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  try {
    const now = Timestamp.now();
    const docRef = await addDoc(collection(db, COLLECTION_NAME), {
      ...data,
      createdAt: now,
      updatedAt: now,
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating video tutorial:', error);
    throw error;
  }
}

/**
 * Update a video tutorial
 */
export async function updateVideoTutorial(
  id: string,
  data: Partial<Omit<VideoTutorial, 'id' | 'createdAt' | 'updatedAt'>>
): Promise<void> {
  try {
    const docRef = doc(db, COLLECTION_NAME, id);
    await updateDoc(docRef, {
      ...data,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error updating video tutorial:', error);
    throw error;
  }
}

/**
 * Delete a video tutorial
 */
export async function deleteVideoTutorial(id: string): Promise<void> {
  try {
    const docRef = doc(db, COLLECTION_NAME, id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting video tutorial:', error);
    throw error;
  }
}

/**
 * Generate YouTube thumbnail URL from video ID
 */
export function getYouTubeThumbnail(youtubeId: string): string {
  return `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`;
}

